import { createStylesObject } from '../../../helpers';
import {
  LeadingStyleGroup,
  GlobalDesignPanelProps,
  StylesGroup,
} from '../../../types';
import { getStylesInfoFromGroup } from './getStylesInfoFromGroup';
import { StyleMetadataEngine } from '../../abstractStyles';
import { getShorthandAtomics } from '../propertyRelationships';
import { STYLE_NAMESPACE } from '../../../../../apis/services/editActionsServices/nativeActions/globalDesign/types';
import { pick } from 'lodash';

/**
 * Creates StyleMetadataEngine for a leading group.
 * Engine can be reused in lobby items and transferred to panel.
 * Scopes engine to only the properties in this group (for group isolation).
 *
 * @param leadingGroup - Leading group to process
 * @param componentsAPI - Components API for CSS variable resolution
 * @returns StyleMetadataEngine - single source of truth for style data
 */
export const getStylesMetadata = (
  group: StylesGroup,
  componentsAPI: GlobalDesignPanelProps['componentsAPI'],
): StyleMetadataEngine => {
  const styles = group.context.query.getStyles();

  const styleDefinitions = createStylesObject(
    group.styleNamespace,
    getStylesInfoFromGroup(group.items),
  );
  const allowedProperties = getAllowedProperties(group);
  const namespace = group.styleNamespace;

  const styleValues = pick(styles[namespace], allowedProperties);

  return new StyleMetadataEngine(
    {
      styleDefinitions,
      styles: createStylesObject(group.styleNamespace, styleValues),
    },
    componentsAPI,
    allowedProperties,
  );
};

const getAllowedProperties = (group: StylesGroup): string[] => {
  const baseProperties = group.items.map((item) => item.styleItemId);

  if (group.styleNamespace !== STYLE_NAMESPACE.CssProperties) {
    return baseProperties;
  }

  const allowed = new Set(baseProperties);

  group.items.forEach((item) => {
    getShorthandAtomics(item.styleItemId).forEach((atomic) => {
      allowed.add(atomic);
    });
  });

  return [...allowed];
};
